import React, { useEffect } from "react";
import { useAtom } from "jotai";
import { ChevronsUpDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { buttonVariants } from "@components/ui/button";
import { Skeleton } from "@components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@components/ui/avatar";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import { selectedOrganisationIdAtom } from "~/state/organisation";
import { cn } from "~/lib/utils";

export const OrganisationSelector: React.FC = () => {
  const [selectedOrganisationId, setSelectedOrganisationId] = useAtom(
    selectedOrganisationIdAtom
  );
  const { currentUser, loading: userLoading } = useCurrentUser();

  const organisations = currentUser?.organisations?.nodes ?? [];

  useEffect(() => {
    if (userLoading) {
      return;
    }

    if (organisations.length > 0) {
      const isSelectedValid =
        selectedOrganisationId &&
        organisations.some((org) => org.id === selectedOrganisationId);

      if (!isSelectedValid) {
        setSelectedOrganisationId(organisations[0].id);
      }
    } else if (!userLoading && organisations.length === 0) {
      if (selectedOrganisationId !== null) {
        setSelectedOrganisationId(null);
      }
    }
  }, [
    userLoading,
    organisations,
    selectedOrganisationId,
    setSelectedOrganisationId,
  ]);

  if (userLoading) {
    return <Skeleton className="h-12 w-[200px] rounded-lg" />;
  }

  const activeOrganisation = organisations.find(
    (org) => org.id === selectedOrganisationId
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          buttonVariants({ variant: "ghost", size: "lg" }),
          "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground h-12 gap-2 px-2"
        )}
      >
        <div className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground">
          <Avatar className="h-8 w-8 rounded-lg">
            {activeOrganisation?.logoUrl && (
              <AvatarImage
                src={activeOrganisation.logoUrl}
                alt={activeOrganisation.name}
              />
            )}
            <AvatarFallback className="rounded-lg">
              {activeOrganisation?.name?.slice(0, 2).toUpperCase() ?? "??"}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate font-semibold">
            {activeOrganisation?.name ?? "Select Organisation"}
          </span>
          <span className="truncate text-xs font-normal text-muted-foreground">
            Active organisation
          </span>
        </div>
        <ChevronsUpDown className="ml-auto size-4 opacity-50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        align="end"
        sideOffset={4}
      >
        {organisations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => setSelectedOrganisationId(org.id)}
            className="gap-2 p-2"
          >
            <div className="flex size-6 items-center justify-center rounded-sm border">
              <Avatar className="h-6 w-6 rounded-sm">
                {org.logoUrl && <AvatarImage src={org.logoUrl} alt={org.name} />}
                <AvatarFallback className="rounded-sm text-xs">
                  {org.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
            {org.name}
            {selectedOrganisationId === org.id && (
              <Check className="ml-auto size-4" />
            )}
          </DropdownMenuItem>
        ))}
        {organisations.length === 0 && (
          <DropdownMenuItem disabled>No organisations found</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
